import { useState } from 'react';
import { changeEmail, changePassword, sendVerificationEmail, setAccountName } from '../api.js';
import { ACCOUNT_NAME_MAX } from '../config.js';
import { useToast } from '../Toaster.jsx';

// The account itself: the address it is reached at and the password it is
// opened with. Everything else on this site is about what a display shows.
//
// Both forms have two shapes now, and which one they take is decided by the
// server rather than guessed at here. An account created by a link has no
// password, so it is not asked for one and the password form reads as setting
// a first one rather than changing an existing one. And an address change is
// no longer a change: it sends a link to the new address and nothing moves
// until that link is followed.
export default function AccountPanel({ me, onAccountChanged }) {
  const notify = useToast();
  const account = me?.account;
  const impersonated = Boolean(me?.impersonatedBy);
  const hasPassword = Boolean(me?.hasPassword);
  const verified = Boolean(me?.emailVerified);

  const [name, setName] = useState(account?.name || '');
  const [emailForm, setEmailForm] = useState({ email: account?.email || '', password: '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  // The address a confirmation was sent to, if one is outstanding. It is the
  // only sign that anything happened - the account still reads as the old
  // address, correctly, because it still is the old address.
  const [pending, setPending] = useState('');
  const [busy, setBusy] = useState('');

  async function handleName(event) {
    event.preventDefault();
    setBusy('name');
    try {
      await setAccountName(name);
      notify('Saved.');
      // The shell prints this and the shared link is built from it, so the
      // change has to travel further than this form.
      onAccountChanged?.();
    } catch (error) {
      notify(`Could not save your name: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function handleEmail(event) {
    event.preventDefault();
    setBusy('email');
    try {
      const result = await changeEmail(emailForm.email, emailForm.password);
      setPending(result.email);
      setEmailForm({ ...emailForm, password: '' });
      notify(`Confirmation sent to ${result.email}.`);
    } catch (error) {
      notify(`Could not change your email: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function handleVerify() {
    setBusy('verify');
    try {
      const result = await sendVerificationEmail();
      if (result.verified) {
        notify('That address is already confirmed.');
        onAccountChanged?.();
        return;
      }
      notify(`Confirmation sent to ${result.email}.`);
    } catch (error) {
      notify(`Could not send that: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function handlePassword(event) {
    event.preventDefault();
    // Checked here rather than at the server, because the second field exists
    // only to catch a typo in the first and the server never sees it.
    if (passwordForm.next !== passwordForm.confirm) {
      notify('Those two passwords do not match.', { tone: 'error' });
      return;
    }

    setBusy('password');
    try {
      const result = await changePassword(passwordForm.current, passwordForm.next);
      notify(
        result.created
          ? 'Password set. You can now sign in with it as well as by link.'
          : 'Password changed. Any other browser signed in as you has been signed out.'
      );
      setPasswordForm({ current: '', next: '', confirm: '' });
      // The sidebar does not show this, but hasPassword decides the shape of
      // this very form - so the shell has to hear about it.
      onAccountChanged?.();
    } catch (error) {
      notify(`Could not change your password: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
    }
  }

  if (impersonated) {
    return (
      <section className="panel">
        <h2>Account</h2>
        <p className="panel-hint">
          You are acting as <strong>{account?.email}</strong> through an impersonation link.
          Their address and password can only be changed by them, from their own sign-in.
        </p>
      </section>
    );
  }

  return (
    <>
      <form className="panel" onSubmit={handleName}>
        <h2>Your name</h2>
        <p className="panel-hint">
          What a shared display calls itself. Send somebody the link under Displays and
          it arrives in their messages as{' '}
          <strong>{name.trim() ? `${name.trim()}’s Litbyt` : 'A Litbyt bookshelf'}</strong>,
          which is the only place this is used.
        </p>

        <label className="field">
          <span>Name</span>
          <input
            type="text"
            required
            maxLength={ACCOUNT_NAME_MAX}
            autoComplete="given-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <button type="submit" className="save-button" disabled={busy === 'name' || !name.trim()}>
          {busy === 'name' ? 'Saving…' : 'Save'}
        </button>
      </form>

      <form className="panel" onSubmit={handleEmail}>
        <h2>Email address</h2>
        <p className="panel-hint">
          This is what you sign in with — we email a link to it. Changing it sends a
          confirmation to the new address, and nothing moves until you follow it.
        </p>

        <p className="panel-hint account-status">
          {verified ? (
            <>
              <strong>{account?.email}</strong> is confirmed.
            </>
          ) : (
            <>
              <strong>{account?.email}</strong> has not been confirmed yet.{' '}
              <button
                type="button"
                className="inline-link"
                onClick={handleVerify}
                disabled={busy === 'verify'}
              >
                {busy === 'verify' ? 'Sending…' : 'Send me a link'}
              </button>
            </>
          )}
        </p>

        {pending && (
          <p className="panel-hint account-status">
            Waiting on <strong>{pending}</strong> to be confirmed. Until then this account
            stays on {account?.email}, and the link expires in twenty minutes.
          </p>
        )}

        <label className="field">
          <span>Email address</span>
          <input
            type="email"
            required
            value={emailForm.email}
            onChange={(event) => setEmailForm({ ...emailForm, email: event.target.value })}
          />
        </label>
        {hasPassword && (
          <label className="field">
            <span>Your current password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={emailForm.password}
              onChange={(event) => setEmailForm({ ...emailForm, password: event.target.value })}
            />
          </label>
        )}
        <button type="submit" className="save-button" disabled={busy === 'email'}>
          {busy === 'email' ? 'Sending…' : 'Send a confirmation link'}
        </button>
      </form>

      <form className="panel" onSubmit={handlePassword}>
        <h2>{hasPassword ? 'Password' : 'Set a password'}</h2>
        <p className="panel-hint">
          {hasPassword
            ? 'Changing this signs out every other browser signed in as you, which is how you take the account back if somebody else has it.'
            : 'You sign in by email link, which needs no password. Setting one adds a second way in — it does not replace the link.'}
        </p>
        {hasPassword && (
          <label className="field">
            <span>Your current password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={passwordForm.current}
              onChange={(event) => setPasswordForm({ ...passwordForm, current: event.target.value })}
            />
          </label>
        )}
        <label className="field">
          <span>{hasPassword ? 'New password' : 'Password'}</span>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={passwordForm.next}
            onChange={(event) => setPasswordForm({ ...passwordForm, next: event.target.value })}
          />
          <small className="field-hint">At least eight characters.</small>
        </label>
        <label className="field">
          <span>{hasPassword ? 'New password again' : 'Password again'}</span>
          <input
            type="password"
            required
            autoComplete="new-password"
            value={passwordForm.confirm}
            onChange={(event) => setPasswordForm({ ...passwordForm, confirm: event.target.value })}
          />
        </label>
        <button type="submit" className="save-button" disabled={busy === 'password'}>
          {busy === 'password' ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
        </button>
      </form>
    </>
  );
}
